import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Keeps everything below an element visually still while that element changes height.
 *
 * The search hero collapses the moment the first filter is applied: the headline, the sentence box
 * and the marque rail all leave at once, which is the better part of four hundred pixels. Without
 * this, that collapse drags the filter panel and the results grid up the screen — under the hands of
 * the person who was mid-way through refining, and usually onto whatever they meant to click next.
 * Browsers do have scroll anchoring of their own, but it gives up on subtrees that are sticky or
 * animating, which describes this page exactly.
 *
 * The correction runs in a layout effect rather than from the observer, and that ordering is the
 * point: layout effects run before the frame's animation callbacks, so a back-button scroll
 * restoration — which is driven by `requestAnimationFrame` — still gets the final say rather than
 * being nudged off course by a correction arriving after it.
 *
 * @param signature Changes exactly when the element is about to change shape, and at no other time.
 */
export function useHeightAnchor(ref: RefObject<HTMLElement | null>, signature: string) {
  const height = useRef<number | null>(null);
  const anchoredAt = useRef(signature);

  /*
    Keeps the recorded height honest between corrections. The marque rail only appears once the
    reference data lands, and a window can be resized at any moment; neither is something anyone
    wants corrected for, but both would otherwise leave a stale measurement behind for the next
    correction to work from.
  */
  useLayoutEffect(() => {
    const node = ref.current;

    if (!node) {
      return;
    }

    const record = () => {
      height.current = node.getBoundingClientRect().height;
    };

    record();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(record);

    observer.observe(node);

    return () => observer.disconnect();
  }, [ref]);

  useLayoutEffect(() => {
    const node = ref.current;
    const before = height.current;

    // Also the guard against a re-run that changed nothing, which in development is every mount:
    // effects are deliberately invoked twice there.
    if (!node || anchoredAt.current === signature) {
      return;
    }

    anchoredAt.current = signature;

    const after = node.getBoundingClientRect().height;
    height.current = after;

    // At the very top of the document there is nothing to preserve: the collapse happens in plain
    // sight rather than under anyone's hands, and clamping at zero would only fight the browser.
    if (before === null || before === after || window.scrollY <= 0) {
      return;
    }

    // `instant` because the page asks for smooth scrolling globally, and animating a correction
    // whose whole purpose is to hide a movement would put the movement straight back.
    window.scrollTo({ top: Math.max(0, window.scrollY - (before - after)), behavior: 'instant' });
  }, [ref, signature]);
}
